import { NavigateFunction } from 'react-router-dom';

import { BillingDA } from '../services/admin/billing.service';
import { BillingInvoice } from '../models/billing.model';
import { GlobalSwal } from '../styles/swal.style';

/**
 * Shared payment-link logic used by three callers:
 * 1. SubscriptionInvoices — invoice list pay button
 * 2. Subscription — "amount due" block on the dashboard
 * 3. ModalUpgradePlan — current plan card payment button
 *
 * Returns the token on success so the caller can decide how to navigate,
 * or null if the request failed (an error swal is shown internally).
 */
export async function requestPaymentLink(
  companyId: string,
  invoiceId: string,
  labels: { error: string; ok: string },
): Promise<string | null> {
  try {
    const response = await BillingDA.createPaymentLink(companyId, invoiceId);
    if (response.status === 200 && response.data?.token) {
      return response.data.token as string;
    }
    GlobalSwal.fire({
      text: response.data?.message ?? response.message ?? labels.error,
      icon: 'error',
      confirmButtonText: labels.ok,
    });
  } catch {
    GlobalSwal.fire({ text: labels.error, icon: 'error' });
  }
  return null;
}

/**
 * Convenience wrapper: request a payment link then navigate to /pay/:token.
 */
export async function payInvoice(
  invoice: BillingInvoice,
  companyId: string,
  navigate: NavigateFunction,
  labels: { error: string; ok: string },
): Promise<boolean> {
  if (!invoice.payable || !invoice.invoice_id) return false;
  const token = await requestPaymentLink(companyId, invoice.invoice_id, labels);
  if (token) {
    navigate(`/pay/${token}`);
    return true;
  }
  return false;
}
